import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProductBySlug } from '../data/productsData';
import { ArrowUpRightIcon } from '../components/Icons';
import RelatedProducts from '../components/RelatedProducts/RelatedProducts';
import './ProductDetailPage.css';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);

  // Scroll to top on load or slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return (
      <div className="product-not-found">
        <h2>Product Not Found</h2>
        <p>The requested machine model could not be found.</p>
        <Link to="/" className="back-home-btn">Return to Home</Link>
      </div>
    );
  }

  return (
    <main className="product-detail-page">
      <div className="product-detail-container">
        
        {/* Top Header Row: Breadcrumb & Title (Left) + Top Quote Button (Right) */}
        <div className="product-header-row">
          <div className="product-title-meta">
            <span className="product-breadcrumb">{product.breadcrumb}</span>
            <h1 className="product-main-title">{product.title}</h1>
            <p className="product-subtitle">{product.subtitle}</p>
          </div>

          <div className="product-top-cta">
            <button className="quote-btn" onClick={() => navigate('/contact')}>
              <span>Get a Quote</span>
              <span className="arrow-circle-badge">
                <ArrowUpRightIcon size={16} color="#F9541B" />
              </span>
            </button>
          </div>
        </div>

        {/* Big Hero Machine Image */}
        <div className="product-hero-image-wrapper">
          <img 
            src={product.mainImage} 
            alt={product.title} 
            className="product-hero-img" 
          />
        </div>

        {/* 2-Column Section: Left Content (Scrolls) vs Right Feature Cards (Sticky) */}
        <div className="product-content-grid">
          
          {/* LEFT COLUMN: Main Description, Detail Images, Bullets & Bottom CTA */}
          <div className="product-left-col">
            
            {/* Section 1: Intro Heading & Paragraphs */}
            <div className="product-text-block">
              <h2 className="product-section-heading">{product.introHeading}</h2>
              <p className="product-paragraph">{product.introText1}</p>
              <p className="product-paragraph">{product.introText2}</p>
            </div>

            {/* Detail Machine Image 1 */}
            {product.detailImage1 && (
              <div className="product-detail-img-box">
                <img src={product.detailImage1} alt={product.title + ' Detail 1'} className="product-detail-img" />
              </div>
            )}

            {/* Section 1: Precision Panel Cutting & Bullets */}
            {product.section1 && (
              <div className="product-text-block">
                <h3 className="product-subsection-heading">{product.section1.heading}</h3>
                <p className="product-paragraph">{product.section1.description}</p>

                {(product.section1.bullets || product.section1.benefits) && (
                  <ul className="product-bullets-list">
                    {(product.section1.bullets || product.section1.benefits).map((bullet, idx) => (
                      <li key={idx} className="bullet-item">
                        <span className="bullet-label">• {bullet.title}</span>
                        <p className="bullet-desc">{bullet.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Detail Machine Image 2 */}
            {product.detailImage2 && (
              <div className="product-detail-img-box">
                <img src={product.detailImage2} alt={product.title + ' Detail 2'} className="product-detail-img" />
              </div>
            )}

            {/* Section 2: Robust Construction & Key Bullets */}
            {product.section2 && (
              <div className="product-text-block">
                <h3 className="product-subsection-heading">{product.section2.heading}</h3>
                <p className="product-paragraph">{product.section2.description}</p>

                {(product.section2.bullets || product.section2.benefits) && (
                  <ul className="product-bullets-list">
                    {(product.section2.bullets || product.section2.benefits).map((bullet, idx) => (
                      <li key={idx} className="bullet-item">
                        <span className="bullet-label">• {bullet.title}</span>
                        <p className="bullet-desc">{bullet.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Detail Machine Image 3 (NEW!) */}
            {product.detailImage3 && (
              <div className="product-detail-img-box">
                <img src={product.detailImage3} alt={product.title + ' Detail 3'} className="product-detail-img" />
              </div>
            )}

            {/* Section 3: Versatile Material Processing & Bullets (NEW!) */}
            {product.section3 && (
              <div className="product-text-block">
                <h3 className="product-subsection-heading">{product.section3.heading}</h3>
                <p className="product-paragraph">{product.section3.description}</p>

                {(product.section3.bullets || product.section3.benefits) && (
                  <ul className="product-bullets-list">
                    {(product.section3.bullets || product.section3.benefits).map((bullet, idx) => (
                      <li key={idx} className="bullet-item">
                        <span className="bullet-label">• {bullet.title}</span>
                        <p className="bullet-desc">{bullet.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Bottom Get a Quote Button */}
            <div className="product-bottom-cta">
              <button className="quote-btn" onClick={() => navigate('/contact')}>
                <span>Get a Quote</span>
                <span className="arrow-circle-badge">
                  <ArrowUpRightIcon size={16} color="#F9541B" />
                </span>
              </button>
            </div>

          </div>

          {/* RIGHT COLUMN: Sticky Feature Highlight Cards */}
          <div className="product-right-col-sticky">
            
            {/* Top 2 Feature Cards Grid */}
            <div className="cards-top-row">
              {product.stickyCards && product.stickyCards.slice(0, 2).map((card, idx) => (
                <div key={idx} className="feature-card-half">
                  <p className="feature-card-text">{card.text}</p>
                  <h3 className="feature-card-title">{card.title}</h3>
                </div>
              ))}
            </div>

            {/* Bottom 1 Wide Feature Card */}
            {product.stickyCards && product.stickyCards[2] && (
              <div className="feature-card-full">
                <p className="feature-card-text">{product.stickyCards[2].text}</p>
                <h3 className="feature-card-title">{product.stickyCards[2].title}</h3>
              </div>
            )}

          </div>

        </div>

      </div>

      {/* Explore Related Machines Section */}
      <RelatedProducts currentSlug={product.slug} />
    </main>
  );
};

export default ProductDetailPage;
